import { useState } from "react";
import { Link } from "react-router-dom";
import { RadialBar, RadialBarChart, PolarAngleAxis } from "recharts";
import { ROUTES } from "@/config/links";
import { trackCta } from "@/lib/analytics";
import * as api from "../NextPresentationReadinessCheck/api";
import { ResultBox } from "@/components/diagnostics/ResultBox";

const CORE_SECTION_TITLE = "Your Communication Presence Map";

const CategoryBar = ({ category }) => (
  <div data-testid={`ep-category-${category.categoryId}`} className="py-2.5">
    <div className="flex items-center justify-between mb-1.5">
      <span className="font-body text-sm font-medium text-ink">{category.name}</span>
      <span className="font-body text-xs text-ink-soft">
        {category.percentage}% · {category.tierLabel}
      </span>
    </div>
    <div className="h-2 rounded-full overflow-hidden bg-paper">
      <div className="h-full rounded-full bg-terracotta" style={{ width: `${category.percentage}%` }} />
    </div>
  </div>
);

/** A single result box — the reusable "card" unit the whole dashboard is built from. */
/**
 * §9 of the v1.2 Source of Truth — the EXACT 11-section order, fixed
 * copy and canonical-result rendering (no independent recalculation —
 * every number/label here is read straight off the backend's
 * `scoring.score_run` payload, never re-derived). 2026-02: re-laid out
 * as a full-page box grid (user's own reference morphology, adapted
 * to the site's `paper`/`ink`/`terracotta` palette) — same content,
 * same data-testids, same donut + bar-chart components as before,
 * just reorganized. NOT the PDF (§10, Phase 3) — no full Framework
 * section, no coach's larger bio here.
 */
export const ResultsCard = ({ result, email, runId, misc }) => {
  const [consultationInterest, setConsultationInterestState] = useState(null);
  const [consultationSaving, setConsultationSaving] = useState(false);

  const strongest = result.categories.find((c) => c.categoryId === result.strongestCategoryId);
  const priority = result.categories.find((c) => c.categoryId === result.firstPriorityCategoryId);
  const authenticity = result.categories.find((c) => c.excludedFromOverall);
  const coreCategories = result.categories.filter((c) => !c.excludedFromOverall);

  const isRefinement = priority?.tierId === "strong_foundation";
  const priorityTitle = isRefinement ? misc.priority_title_refinement : misc.priority_title_default;
  const priorityBody = isRefinement ? misc.priority_body_refinement : misc.priority_body_default;

  const stageDescKey = `stage_desc_${result.overall.tierId}`;

  // `results_viewed` is already fired exactly once, guarded, by the
  // parent page (`index.jsx`'s `trackResultsViewed`) the moment this
  // stage renders — this component must never publish it a second
  // time (2026-10-21 fix: testing found this duplicate firing
  // `results_viewed` 3x per completion in dev/StrictMode).

  const handleConsultationAnswer = async (answer) => {
    setConsultationSaving(true);
    try {
      await api.setConsultationInterest(runId, answer);
      setConsultationInterestState(answer);
      capture("consultation_interest_selected", { diagnostic_id: "executive_presence_communication", run_id: runId, answer });
    } catch {
      // Best-effort — the answer still shows locally so the conditional
      // CTA below renders even if the persist call failed transiently.
      setConsultationInterestState(answer);
    } finally {
      setConsultationSaving(false);
    }
  };

  const handleCtaClick = (ctaType, href, targetAssetId = null) => {
    capture("consultation_clicked", { diagnostic_id: "executive_presence_communication", run_id: runId, answer: consultationInterest, cta_type: ctaType });
    trackCta({ ctaId: `ep_results_${ctaType}`, location: "diagnostic_results", href, targetAssetId });
  };

  const CONSULTATION_OPTIONS = [
    { value: "yes", label: misc.consultation_option_yes },
    { value: "maybe", label: misc.consultation_option_maybe },
    { value: "not_right_now", label: misc.consultation_option_not_now },
  ];

  return (
    <div data-testid="ep-results-card" className="max-w-5xl mx-auto px-5 md:px-10 py-10 md:py-16">
      {/* §9.2 Header */}
      <div className="mb-8 max-w-2xl" data-testid="ep-results-header">
        <p className="font-body text-xs uppercase tracking-[0.2em] text-terracotta mb-2">{misc.results_header_eyebrow}</p>
        <p className="font-body text-sm md:text-base leading-relaxed text-ink-soft">{misc.results_header_body}</p>
      </div>

      <div className="space-y-6">
        {/* §9.3 Overall Score — hero box */}
        <ResultBox tone="dark" testId="ep-overall-section" className="flex flex-col md:flex-row items-center gap-6">
          <div className="relative w-36 h-36 shrink-0" data-testid="ep-overall-chart">
            <RadialBarChart
              width={144} height={144} cx={72} cy={72} innerRadius={56} outerRadius={70} barSize={14}
              data={[{ value: result.overall.percentage, fill: "#DA983C" }]}
              startAngle={90} endAngle={-270}
            >
              <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
              <RadialBar dataKey="value" background={{ fill: "rgba(242,235,218,0.18)" }} cornerRadius={8} />
            </RadialBarChart>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-serif text-3xl text-paper">{result.overall.percentage}%</span>
            </div>
          </div>
          <div>
            <p className="font-body text-xs uppercase tracking-[0.2em] mb-1 text-paper/70">{misc.overall_section_title}</p>
            <p className="font-serif text-2xl mb-2 text-paper" data-testid="ep-overall-tier">{result.overall.tierLabel}</p>
            <p className="font-body text-sm leading-relaxed text-paper/85">{misc.overall_section_body}</p>
            <p className="font-body text-sm leading-relaxed text-paper/85 mt-2" data-testid="ep-overall-stage-desc">
              {misc[stageDescKey]}
            </p>
          </div>
        </ResultBox>

        {/* §9.4 Six-Area Map — core categories only, Authenticity excluded */}
        <ResultBox tone="deep" testId="ep-six-area-map">
          <p className="font-serif text-xl text-charcoal mb-1">{misc.six_area_map_title || CORE_SECTION_TITLE}</p>
          <p className="font-body text-sm leading-relaxed text-ink-soft mb-3">{misc.six_area_map_body}</p>
          <div className="divide-y divide-ink/10">
            {coreCategories.map((c) => (
              <CategoryBar key={c.categoryId} category={c} />
            ))}
          </div>
        </ResultBox>

        {/* §9.5 Strongest Area + §9.6 First Development Priority / Next Refinement */}
        <div className="grid md:grid-cols-2 gap-6">
          {strongest && (
            <ResultBox tone="deep" testId="ep-strongest-area">
              <p className="font-body text-xs uppercase tracking-[0.2em] mb-2 text-terracotta">
                {result.strongestTied ? (result.strongestTieLabel || misc.strongest_tie_label) : misc.strongest_area_title}
              </p>
              <p className="font-serif text-xl text-charcoal mb-2">{strongest.name}</p>
              <p className="font-body text-sm leading-relaxed text-ink-soft">{strongest.resultCopy}</p>
            </ResultBox>
          )}
          {priority && (
            <ResultBox testId="ep-priority-area">
              <p className="font-body text-xs uppercase tracking-[0.2em] mb-2 text-terracotta" data-testid="ep-priority-title">
                {priorityTitle}
              </p>
              <p className="font-body text-xs text-ink-soft/80 mb-2">{priorityBody}</p>
              <p className="font-serif text-xl text-charcoal mb-2">{priority.name}</p>
              <p className="font-body text-sm leading-relaxed text-ink-soft">{priority.resultCopy}</p>
              {/* §12 appended Authenticity-low bridge — shown here, never twice */}
              {result.appendedNotes?.map((note, i) => (
                <p key={i} className="font-body text-sm leading-relaxed text-ink-soft italic mt-3" data-testid="ep-appended-note">
                  {note}
                </p>
              ))}
            </ResultBox>
          )}
        </div>

        {/* §9.7 Authenticity & Congruence — cross-cutting, visually distinct (mustard accent) */}
        {authenticity && (
          <ResultBox testId="ep-authenticity-section" className="border-mustard/40 border-2">
            <p className="font-serif text-xl text-charcoal mb-1.5">{misc.authenticity_section_title}</p>
            <p className="font-body text-sm leading-relaxed text-ink-soft mb-3">{misc.authenticity_section_body}</p>
            <p className="font-body text-sm font-semibold text-terracotta mb-1" data-testid="ep-authenticity-score">
              {authenticity.percentage}% · {authenticity.tierLabel}
            </p>
            <p className="font-body text-sm leading-relaxed text-ink-soft">{authenticity.resultCopy}</p>
          </ResultBox>
        )}

        {/* §9.8 What this result tells you */}
        <ResultBox tone="deep" testId="ep-measurement-boundary">
          <p className="font-serif text-lg text-charcoal mb-1.5">{misc.measurement_boundary_title}</p>
          <p className="font-body text-sm leading-relaxed text-ink-soft whitespace-pre-line">{misc.measurement_boundary_body}</p>
        </ResultBox>

        {/* §9.9 Consultation-interest question (LOCKED) */}
        <ResultBox testId="ep-consultation-question">
          <p className="font-serif text-xl text-charcoal mb-4">{misc.consultation_question}</p>
          <div className="space-y-2.5">
            {CONSULTATION_OPTIONS.map((opt) => {
              const isSelected = consultationInterest === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`ep-consultation-option-${opt.value}`}
                  disabled={consultationSaving}
                  onClick={() => handleConsultationAnswer(opt.value)}
                  className={`w-full text-left rounded-xl px-5 py-3.5 font-body text-sm transition-colors duration-150 disabled:opacity-60 ${
                    isSelected ? "bg-terracotta text-paper" : "bg-paper-deep text-ink hover:bg-paper-deep/70"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          {/* §9.9 answer-conditional CTA / copy */}
          {consultationInterest === "yes" && (
            <div className="mt-5" data-testid="ep-consultation-cta-yes">
              <Link to={ROUTES.bookExecutivePresence} onClick={() => handleCtaClick("book_executive_presence", ROUTES.bookExecutivePresence, "booking_executive_presence")} data-testid="ep-book-consultation-cta">
                <button type="button" className="w-full py-4 rounded-full font-body text-sm font-medium bg-terracotta text-paper hover:bg-terracotta-dark transition-colors">
                  {misc.book_consultation_button}
                </button>
              </Link>
            </div>
          )}
          {consultationInterest === "maybe" && (
            <div className="mt-5" data-testid="ep-consultation-cta-maybe">
              <p className="font-body text-sm leading-relaxed text-ink-soft mb-3">{misc.consultation_maybe_body}</p>
              <Link to={ROUTES.executivePresenceDevelopment} onClick={() => handleCtaClick("explore_ep_development", ROUTES.executivePresenceDevelopment)} data-testid="ep-explore-coaching-cta">
                <button type="button" className="w-full py-4 rounded-full font-body text-sm font-medium border border-ink/30 text-ink hover:border-terracotta hover:text-terracotta transition-colors">
                  {misc.consultation_maybe_cta}
                </button>
              </Link>
            </div>
          )}
        </ResultBox>

        {/* §9.10 Full report confirmation */}
        <ResultBox tone="deep" testId="ep-email-notice" className="text-sm">
          <p className="font-body text-sm leading-relaxed text-ink">
            {(() => {
              const [before, after] = (misc.full_report_body || "").split("{email}");
              return (
                <>
                  {before}
                  <strong data-ph-mask>{email}</strong>
                  {after}
                </>
              );
            })()}
          </p>
        </ResultBox>

        {/* §9.11 About Your Coach */}
        <ResultBox testId="ep-coach-bio">
          <p className="font-serif text-lg text-charcoal mb-2">{misc.coach_bio_heading}</p>
          <p className="font-body text-sm leading-relaxed text-ink-soft mb-2">{misc.coach_bio_results_page_p1}</p>
          <p className="font-body text-sm leading-relaxed text-ink-soft mb-2">{misc.coach_bio_results_page_p2}</p>
          <p className="font-body text-sm leading-relaxed text-ink-soft">{misc.coach_bio_results_page_p3}</p>
        </ResultBox>
      </div>
    </div>
  );
};
